import React from "react";
import PropTypes from "prop-types";
import * as DateUtils from "utils/dateUtil";
import { DataGrid } from "@mui/x-data-grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {
  Approve as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "components/icons";
import CustomNoRowsOverlay from "app/components/overlays/CustomNoRowsOverlay";
import LinearProgress from "@mui/material/LinearProgress";
import MDBox from "components/MDBox";
import { FilterAndExportGridToolbar } from "app/components/overlays/FilterAndExportGridToolbar";
import { isEmpty } from "utils/validationUtil";
import "./leave-plans.css";

// Whether the leave has started yet is a calendar-day question. startDate arrives as a date-only
// string and new Date() is an instant, so the raw comparison flipped APPLIED -> AVAILED at 05:30
// IST rather than at midnight.
function showLeaveStatus(startDate, status) {
    if (status === "ADDED")
        return DateUtils.startOfLocalDay(startDate) >= DateUtils.startOfLocalDay(new Date()) ? "APPLIED" : "AVAILED";
    return status;
}

function showLeaveDuration(row) {
    if (row.leaveDuration === "FIRST_HALF") return "Half Day (AM)";
    if (row.leaveDuration === "SECOND_HALF") return "Half Day (PM)";
    return row.totalWorkingDays + (row.totalWorkingDays > 1 ? " days" : " day");
}

const baseColumns = [
    { field: "clientCode", headerName: "Client", width: 75 },
    { field: "memberName", headerName: "Member", width: 250 },
    {
        field: "startDate",
        headerName: "Start Date",
        width: 160,
        valueGetter: (value, row) =>
            DateUtils.getDateInDDMMYYYYFormat(row.startDate) + " (" + DateUtils.getDayName(row.startDate) + ")",
    },
    {
        field: "totalWorkingDays",
        headerName: "Duration",
        width: 120,
        valueGetter: (value, row) => showLeaveDuration(row),
    },
    {
        field: "status",
        headerName: "Status",
        width: 110,
        valueGetter: (value, row) => showLeaveStatus(row.startDate, row.status),
    },
    {
        field: "approvalStatus",
        headerName: "Approval",
        width: 120,
    },
    { field: "reason", headerName: "Reason", width: 350 },
];

const ActionCell = ({ row, actions }) => {
    const [pending, setPending] = React.useState(false);

    const wrap = (fn) => async () => {
        setPending(true);
        try {
            await fn(row);
        } finally {
            setPending(false);
        }
    };

    // Approve/Reject only apply to a pending leave. Edit/Delete show whenever the
    // parent supplies the handler (leaves are auto-approved, so they're never SUBMITTED).
    const isSubmitted = row.approvalStatus === "SUBMITTED";

    return (
        <MDBox>
            {isSubmitted && actions.onApprove && (
                <Tooltip title="Approve">
                    <span>
                        <IconButton
                            size="small"
                            color="success"
                            disabled={pending}
                            onClick={wrap(actions.onApprove)}
                            aria-label="approve"
                        >
                            <CheckIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            )}
            {isSubmitted && actions.onReject && (
                <Tooltip title="Reject">
                    <span>
                        <IconButton
                            size="small"
                            color="error"
                            disabled={pending}
                            onClick={wrap(actions.onReject)}
                            aria-label="reject"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            )}
            {actions.onEdit && (
                <Tooltip title="Edit">
                    <span>
                        <IconButton
                            size="small"
                            color="info"
                            onClick={() => actions.onEdit(row)}
                            aria-label="edit"
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            )}
            {actions.onDelete && (
                <Tooltip title="Delete">
                    <span>
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => actions.onDelete(row)}
                            aria-label="delete"
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </MDBox>
    );
};

ActionCell.propTypes = {
    row: PropTypes.object.isRequired,
    actions: PropTypes.shape({
        onApprove: PropTypes.func,
        onReject: PropTypes.func,
        onEdit: PropTypes.func,
        onDelete: PropTypes.func,
    }).isRequired,
};

export const LeavePlanDataGridBase = (props) => {
    const [pageSize, setPageSize] = React.useState(10);

    const getData = () => {
        if (isEmpty(props.leavePlansData) || props.leavePlansData.length === 0) return [];
        return props.leavePlansData;
    };

    const columns = React.useMemo(() => {
        if (!props.actions) return baseColumns;
        return [
            ...baseColumns,
            {
                field: "actions",
                headerName: "Actions",
                width: 110,
                sortable: false,
                filterable: false,
                renderCell: (params) => <ActionCell row={params.row} actions={props.actions} />,
            },
        ];
    }, [props.actions]);

    const cellClassName = (params) => {
        if (params.field === "status") {
            return `leave-plan-app-theme--${showLeaveStatus(params.row.startDate, params.row.status).toLowerCase()}`;
        }
        if (params.field === "approvalStatus" && params.row.approvalStatus) {
            return `leave-plan-app-theme--${String(params.row.approvalStatus).toLowerCase()}`;
        }
        return "";
    };

    return (
        <MDBox pt={3} py={3} px={2} height={500}>
            <DataGrid
                density="compact"
                rows={getData()}
                columns={columns}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[5, 10, 20]}
                loading={props.loading}
                components={{
                    Toolbar: FilterAndExportGridToolbar,
                    LoadingOverlay: LinearProgress,
                    NoRowsOverlay: CustomNoRowsOverlay,
                }}
                getCellClassName={cellClassName}
            />
        </MDBox>
    );
};

LeavePlanDataGridBase.propTypes = {
    leavePlansData: PropTypes.array,
    // Drives the grid's LoadingOverlay, which was wired up but never switched on: a refetch that
    // returns the same rows used to look like nothing had happened (HRMS-264 review).
    loading: PropTypes.bool,
    actions: PropTypes.shape({
        onApprove: PropTypes.func,
        onReject: PropTypes.func,
        onEdit: PropTypes.func,
        onDelete: PropTypes.func,
    }),
};
